import { useScriptSettings } from "@/contexts/ScriptSettingsContext";
import { useI18n } from "@/contexts/I18nContext";
import { Check } from "lucide-react";

/**
 * Reusable Script (Mushaf) picker — two preview tiles rendering "الله" in
 * the Uthmani vs Indo-Pak font so the user can self-identify their childhood
 * reading style at a glance.
 *
 * Used both inside the onboarding sub-step (compact=false) and the Settings
 * page (compact=true, no headline so it slots into the page chrome).
 */
export default function ScriptPicker({ compact = false, onChosen }) {
  const { script, setScript } = useScriptSettings();
  const { t } = useI18n();

  const choose = (val) => {
    setScript(val, { explicit: true });
    onChosen && onChosen(val);
  };

  return (
    <div className="w-full" data-testid="script-picker">
      {!compact && (
        <>
          <div className="text-xs uppercase tracking-[0.22em] text-[#D97706] font-semibold">{t("settings.script.choose")}</div>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#1F2937] mt-2">{t("settings.script.heading")}</h2>
          <p className="text-sm text-[#4B5563] mt-2 max-w-xl leading-relaxed">
            {t("settings.script.body")}
          </p>
        </>
      )}
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <PreviewCard
          value="uthmani"
          active={script === "uthmani"}
          onClick={() => choose("uthmani")}
          title={t("settings.script.uthmani")}
          subtitle={t("settings.script.uthmani_sub")}
          region={t("settings.script.uthmani_region")}
          previewClass="font-preview-uthmani"
        />
        <PreviewCard
          value="indopak"
          active={script === "indopak"}
          onClick={() => choose("indopak")}
          title={t("settings.script.indopak")}
          subtitle={t("settings.script.indopak_sub")}
          region={t("settings.script.indopak_region")}
          previewClass="font-preview-indopak"
        />
      </div>
      <div className="mt-3 text-[11px] text-[#4B5563]">
        {t("settings.script.changeable")}
      </div>
    </div>
  );
}

function PreviewCard({ value, active, onClick, title, subtitle, region, previewClass }) {
  return (
    <button
      type="button"
      data-testid={`script-pick-${value}`}
      onClick={onClick}
      aria-pressed={active}
      className={`group relative rounded-2xl bg-white p-5 sm:p-6 text-left transition border-2 ${
        active
          ? "border-[#064E3B] shadow-[0_10px_30px_-15px_rgba(6,78,59,0.35)]"
          : "border-[#E7E0D2] hover:border-[#064E3B] hover:shadow-[0_8px_24px_-12px_rgba(6,78,59,0.18)]"
      }`}>
      {active && (
        <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#064E3B] grid place-items-center" data-testid={`script-pick-${value}-checked`}>
          <Check className="w-3.5 h-3.5 text-white" />
        </span>
      )}
      {/* Visual preview of the canonical word "Allah" in this script's font.
          The .font-preview-* classes are defined in index.css. */}
      <div
        className={`${previewClass} text-5xl sm:text-6xl text-center text-[#064E3B] leading-none py-4 px-2`}
        dir="rtl"
        lang="ar"
        data-testid={`script-pick-${value}-preview`}>
        الله
      </div>
      <div className="mt-3 pt-3 border-t border-[#E7E0D2]">
        <div className="font-heading text-base font-semibold text-[#1F2937]">{title}</div>
        <div className="text-xs text-[#4B5563] mt-0.5">{subtitle}</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] mt-2">{region}</div>
      </div>
    </button>
  );
}
